import i18n from "./i18n";

type ErrorLikeWithResponse = {
  message?: unknown;
  response?: {
    status?: unknown;
    data?: {
      detail?: unknown;
    };
  };
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function extractErrorDetail(error: unknown): string | null {
  if (!isObject(error)) return null;
  const maybeError = error as ErrorLikeWithResponse;
  const detail = maybeError.response?.data?.detail;
  if (typeof detail === "string" && detail.trim().length > 0) return detail;

  if (error instanceof Error) {
    const parsed = parseHttpErrorFromMessage(error.message);
    if (parsed?.detail) return parsed.detail;
  }

  return null;
}

export function parseHttpErrorFromMessage(message: string): { status?: number; detail?: string } | null {
  const match = message.match(/^HTTP (\d+):\s*([\s\S]+)$/);
  if (!match) return null;

  const status = Number.parseInt(match[1], 10);
  const body = match[2].trim();

  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    if (typeof parsed.detail === "string" && parsed.detail.trim().length > 0) {
      return { status, detail: parsed.detail };
    }
  } catch {
    // body is plain text
  }

  return { status, detail: body };
}

export function isMemberNotRegisteredError(error: unknown): boolean {
  if (error instanceof Error) {
    const parsed = parseHttpErrorFromMessage(error.message);
    if (parsed?.status === 404 && parsed.detail) {
      const normalized = parsed.detail.toLowerCase();
      return (
        normalized.includes("não encontrado") ||
        normalized.includes("not found") ||
        normalized.includes("no encontrado")
      );
    }
  }

  if (hasErrorStatus(error, 404)) {
    const detail = extractErrorDetail(error);
    if (!detail) return false;
    const normalized = detail.toLowerCase();
    return (
      normalized.includes("não encontrado") ||
      normalized.includes("not found") ||
      normalized.includes("no encontrado")
    );
  }

  return false;
}

export function extractErrorMessage(error: unknown, fallback: string): string {
  const detail = extractErrorDetail(error);
  if (detail) {
    // Tenta traduzir se parecer uma chave de tradução (ex: "errors.owner_only")
    // Substituímos "." por ":" para o padrão do i18next de namespaces
    const i18nKey = detail.includes(".") ? detail.replace(".", ":") : detail;

    if (i18n.exists(i18nKey)) {
      return i18n.t(i18nKey);
    }

    // Tenta no namespace de errors se for uma chave simples
    if (!detail.includes(".") && !detail.includes(":") && i18n.exists(`errors:${detail}`)) {
      return i18n.t(`errors:${detail}`);
    }

    return detail;
  }

  if (error instanceof Error && error.message.trim().length > 0) return error.message;
  return fallback;
}

export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException) return error.name === "AbortError";
  if (error instanceof Error) return error.name === "AbortError";
  return false;
}

export function hasErrorStatus(error: unknown, statusCode: number): boolean {
  if (!isObject(error)) return false;
  const maybeError = error as ErrorLikeWithResponse;
  if (typeof maybeError.response?.status === "number") {
    return maybeError.response.status === statusCode;
  }
  if (typeof maybeError.message === "string") {
    return maybeError.message.includes(`HTTP ${statusCode}`);
  }
  return false;
}
